import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments';
import { Observable, throwError as _throw } from 'rxjs';
import { map, catchError, debounceTime } from 'rxjs/operators';
import { ThirdPartyEvaluationListItem } from '../models/thirdparty-evaluation-list-item';
import { ThirdPartyEvaluation } from '../models/thirdparty-evaluation';
import { ThirdPartyEvaluationFormAnswer } from '../models/thirdparty-evaluation-form-answer';
import { ThirdPartyEvaluationResult } from '../models/thirdparty-evaluation-result';
import { ThirdPartyEvaluationConfirmation } from '../models/thirdparty-evaluation-confirmation';
import { ThirdpartyPendingEvaluationFilter } from '../models/thirdparty-pending-evaluation-filter';
import { ThirdPartyEvaluationStatus } from '../models/thirdparty-evaluation-status';
import { ThirdPartyTracingActionExpiration } from '../models/thridparty-tracingaction-expiration';
import { ThirdPartyEvaluationExplore } from '../models/thirdparty-evaluation-explore';
import { ThirdPartyEvaluationApprovementRequest } from '../models/thirdparty-evaluation-approvement-request';
import { ThirdPartyEvaluationFiles } from '../models/thirdparty-evaluation-files';
import { ThirdPartyTracingActionListItem } from '../models/thirdparty-tracingaction-listitem';
import { TracingPlanSpecialTaskDetail } from '../models/tracing-plan-special-task-detail';
import { ThirdPartyEvaluationStatusItem } from '../models/thirdparty-evaluation-status-item';
import { ISelectionItem } from '../models/selection-item';

@Injectable({
  providedIn: 'root'
})
export class ThirdPartyEvaluationService {
  private apiEndPoint: string;

  constructor(
    private _http: HttpClient,
    private translate: TranslateService
  ) {
    this.apiEndPoint = environment.apiUrl;
  }

  getEvaluationResult(id: any): Observable<ThirdPartyEvaluationResult> {
    const params = new HttpParams()
      .append("thirdPartyEvaluationId", id);

    return this._http
      .get(`${this.apiEndPoint}/FormHandler/GetLastSuggestionOrCalulate`, { params })
      .pipe(
        map((res: any) => {
          let evaluationResult = Object.assign(new ThirdPartyEvaluationResult(), res.suggestionResultDto) as ThirdPartyEvaluationResult;
          return evaluationResult;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getEvaluationApprovementRequest(id: any): Observable<ThirdPartyEvaluationApprovementRequest> {
    const params = new HttpParams()
      .append("thirdPartyEvaluationId", id);

    return this._http
      .get<ThirdPartyEvaluationApprovementRequest>(`${this.apiEndPoint}/FormHandler/GetProposalResult`, { params })
      .pipe(
        map(res => {
          let evaluationApprovementRequest = Object.assign(new ThirdPartyEvaluationApprovementRequest(), res) as ThirdPartyEvaluationApprovementRequest;
          return evaluationApprovementRequest;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getOnGoingEvaluation(id: any, isViewDetailMode: boolean = false): Observable<any> {
    const params = new HttpParams()
      .append("isViewDetailMode", String(isViewDetailMode))
      .append("thirdPartyEvaluationId", id);

    return this._http
      .get(`${this.apiEndPoint}/FormHandler/GetLastSuggestionOrCalulate`, { params })
      .pipe(
        map((res: any) => {
          if (res.calculateResultDto) {
            return Object.assign(new ThirdPartyEvaluation(), res.calculateResultDto) as ThirdPartyEvaluation;
          }
          else if (res.suggestionResultDto) {
            return Object.assign(new ThirdPartyEvaluationResult(), res.suggestionResultDto) as ThirdPartyEvaluationResult;
          }
          // ToDo #sbaleani: devolver todo el dto con el status
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getEvaluationStatus(id: any): Observable<{ evaluationStateDto: ThirdPartyEvaluationStatus, suggestionResultDto: any}> {
    const params = new HttpParams()
      .append("thirdPartyEvaluationId", id);

    return this._http
      .get(`${this.apiEndPoint}/FormHandler/GetLastSuggestionOrCalulate`, { params })
      .pipe(
        map((res: any) => {
          return {
            evaluationStateDto: Object.assign(new ThirdPartyEvaluationStatus(), res.evaluationStateDto) as ThirdPartyEvaluationStatus,
            suggestionResultDto: res.suggestionResultDto,
          };
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getPagedPendingEvaluations(pendingEvaluationFilter: ThirdpartyPendingEvaluationFilter): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/ThirdParty/GetPendingEvaluationForApprovement`, pendingEvaluationFilter)
      .pipe(
        debounceTime(5000),
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getCompletedStageForm(stageId: any): Observable<any> {
    const params = new HttpParams()
      .append("stageId", stageId);

    return this._http
      .get(`${this.apiEndPoint}/FormHandler/GetLastSuggestionOrCalulate`, { params })
      .pipe(
        map((res: any) => {
          if (res.calculateResultDto) {
            return Object.assign(new ThirdPartyEvaluation(), res.calculateResultDto) as ThirdPartyEvaluation;
          }
          else if (res.suggestionResultDto) {
            return Object.assign(new ThirdPartyEvaluationResult(), res.suggestionResultDto) as ThirdPartyEvaluationResult;
          }
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  confirmEvaluationResult(evaluationConfirmation: ThirdPartyEvaluationConfirmation): Observable<ThirdPartyEvaluationStatus> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/ConfirmAndGenerateNewForm`, evaluationConfirmation)
      .pipe(
        map((res: any) => {
          return Object.assign(new ThirdPartyEvaluationStatus(), res.evaluationStateDto) as ThirdPartyEvaluationStatus;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  confirmEvaluationApprovementRequest(evaluationConfirmation: ThirdPartyEvaluationConfirmation): Observable<ThirdPartyEvaluationStatus> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/ApprovalConfirmation`, evaluationConfirmation)
      .pipe(
        map((res: any) => {
          return Object.assign(new ThirdPartyEvaluationStatus(), res.evaluationStateDto) as ThirdPartyEvaluationStatus;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  startEvaluation(id: any): Observable<{result: ThirdPartyEvaluation, state: ThirdPartyEvaluationStatus}> {
    const params = new HttpParams()
      .append("thirdPartyId", id);

    return this._http
      .get<ThirdPartyEvaluation>(`${this.apiEndPoint}/FormHandler/InitializeEvaluation`, { params })
      .pipe(
        map((res: any) => {
          return {
            result: Object.assign(new ThirdPartyEvaluation(), res.calculateResultDto) as ThirdPartyEvaluation,
            state: Object.assign(new ThirdPartyEvaluationStatus(), res.evaluationStateDto) as ThirdPartyEvaluationStatus,
          };
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  calculateForm(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/CalculateForm`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  calculateFormForReviewerStage(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/CalculateFormForReviewerStage`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  sendFormToTenant(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/SwornDeclarationConfirm`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Guardado parcial del Tenant (YPF)
  saveForm(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/PartialSave`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Guardado parcial del 3P
  savePartialFormExternal(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/ExternalUserPartialSave`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }
  // Guardado completo antes de DDJJ del 3P
  saveCompletedFormExternal(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/ExternalUserSave`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getTracingActionExpirationTypes(): Observable<ThirdPartyTracingActionExpiration[]> {
    return this._http
      .get<ThirdPartyTracingActionExpiration[]>(`${this.apiEndPoint}/ThirdParty/GetTimeLapses`)
      .pipe(
        map((res: ThirdPartyTracingActionExpiration[]) => {
          return res;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getLastEvaluationForm(evaluationId): Observable<ThirdPartyEvaluationExplore> {
    return this.getEvaluationForm(evaluationId);
  }

  getEvaluationForm(evaluationId, evaluationFormId?): Observable<ThirdPartyEvaluationExplore> {
    var params = new HttpParams()
      .append("evaluationId", evaluationId);

    if (evaluationFormId != null) {
      params = params.append("evaluationFormId", evaluationFormId);
    }

    return this._http
      .get<ThirdPartyEvaluationExplore>(`${this.apiEndPoint}/ThirdParty/GetLastEvaluationForm`, { params })
      .pipe(
        map((res: ThirdPartyEvaluationExplore) => {
          return Object.assign(new ThirdPartyEvaluationExplore(), res) as ThirdPartyEvaluationExplore;
        }),
        // catchError(err => {
        //   if (err.errorcode) {
        //     return _throw(err.errorcode);
        //   }
        //   return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        // })
      );
  }

  getThirdPartyEvaluationForm(evaluationId, evaluationFormId?): Observable<ThirdPartyEvaluationExplore> {
    var params = new HttpParams()
      .append("evaluationId", evaluationId);

    if (evaluationFormId != null) {
      params = params.append("evaluationFormId", evaluationFormId);
    }

    return this._http
      .get<ThirdPartyEvaluationExplore>(`${this.apiEndPoint}/ThirdParty/GetEvaluationFormForExternaThirdParty`, { params })
      .pipe(
        map((res: ThirdPartyEvaluationExplore) => {
          return Object.assign(new ThirdPartyEvaluationExplore(), res) as ThirdPartyEvaluationExplore;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getDueDiligenceEvaluationForm(evaluationId, evaluationFormId?): Observable<ThirdPartyEvaluationExplore> {
    var params = new HttpParams()
      .append("evaluationId", evaluationId);

    if (evaluationFormId != null) {
      params = params.append("evaluationFormId", evaluationFormId);
    }

    return this._http
      .get<ThirdPartyEvaluationExplore>(`${this.apiEndPoint}/ThirdParty/GetEvaluationFormForExternalDueDiligence`, { params })
      .pipe(
        map((res: ThirdPartyEvaluationExplore) => {
          return Object.assign(new ThirdPartyEvaluationExplore(), res) as ThirdPartyEvaluationExplore;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Reenvia el formulario al tercero
  resendFormToThirdParty(evaluationFiles: ThirdPartyEvaluationFiles): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/CalculateFormWithConversation`, evaluationFiles)
      .pipe(
        map(res => {
          return res;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Envia el formulario a calcular desde Stage 3P
  calculateFormFrom3pStage(evaluationFiles: ThirdPartyEvaluationFiles): Observable<any> {
    return this.resendFormToThirdParty(evaluationFiles);
  }

  getEvaluationFormPdf(thirdPartyId, evaluationId, evaluationFormId): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/ThirdParty/GetEvaluationFormPdf`, { thirdPartyId, evaluationId, evaluationFormId }, { responseType: 'blob' })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  cancelEvaluation(thirdPartyId): Observable<any> {
    const params = new HttpParams()
      .append("thirdPartyId", thirdPartyId);

    return this._http
      .get<any>(`${this.apiEndPoint}/FormHandler/CancelEvaluation`, { params })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  extendEvaluationTerm(thirdPartyId: number): Observable<any> {
    const params = new HttpParams()
      .append("thirdPartyId", thirdPartyId.toString());

    return this._http
      .get<any>(`${this.apiEndPoint}/ThirdParty/ExtendDueDate`, { params })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  restartEvaluation(thirdPartyId: number, stageId: number): Observable<any> {
    const params = new HttpParams()
      .append('thirdPartyId', thirdPartyId.toString())
      .append('stageId', stageId.toString());

    return this._http
      .get<any>(`${this.apiEndPoint}/FormHandler/ModifEvalution`, { params })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getSpecialTaskDetail(specialTask: ThirdPartyTracingActionListItem, evaluationId: number): Observable<TracingPlanSpecialTaskDetail> {
    var params = new HttpParams()
      .append("followUpTaskAnswerId", specialTask.idFromFollowUpTaskAppFormAnswer.toString())
      .append("thirdPartyFormEvaluationId", evaluationId.toString());

    //return this.getEvaluationForm(8,30);

    return this._http
      .get<TracingPlanSpecialTaskDetail>(`${this.apiEndPoint}/Task/GetAnswerTaskRelated`, { params })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getAvailableStatus(): Observable<ThirdPartyEvaluationStatusItem[]> {
    return this._http
      .get<any>(`${this.apiEndPoint}/DashBoard/GetEnumThirdPartyRiskByEvaluationStage`)
      .pipe(
        map(res => {
          const evaluationStatus: ThirdPartyEvaluationStatusItem[] = [];
          for (const key in res) {
            if (res.hasOwnProperty(key)) {
              const element: string = res[key];
              evaluationStatus.push({
                id: Number.parseInt(key),
                description: element
              });
            }
          }
          return evaluationStatus;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getAvailableRoles(): Observable<ISelectionItem[]> {
    return this._http
      .get<ISelectionItem[]>(`${this.apiEndPoint}/Roles/GetRolesDataSourceForClient`)
      .pipe(
        map(res => {
          return res;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

}


